import { KBIcon } from "./KBIcon"
import { Item } from "~/components/ui/item"
import {
	EllipsisIcon,
	HistoryIcon,
	PinIcon,
	ShieldIcon,
	StarIcon,
	Trash2Icon
} from "lucide-react"
import { Link } from "~/components/Link"
import { usePermission } from "~/hooks/usePermission"
import { confirmAction } from "~/lib/native-dialog"
import { TeamPermissions } from "~/lib/permissions"
import { cn, formatDate, shortRelativeTime } from "~/lib/utils"
import { api, type RouterOutputs } from "~/trpc/react"
import { Button } from "../ui/button"
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from "../ui/dropdown-menu"
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger
} from "../ui/tooltip"

export function KBDocumentRow({
	document
}: {
	document: RouterOutputs["knowledgeBase"]["list"][number]
}) {
	const utils = api.useUtils()
	const { hasTeamPermission } = usePermission({ activeTeamId: document.teamId })
	const canEdit = hasTeamPermission(TeamPermissions.EDIT_KNOWLEDGE_BASE)
	const toggleFavorite = api.knowledgeBase.toggleFavorite.useMutation({
		onSuccess: () => {
			utils.knowledgeBase.list.invalidate({ teamId: document.teamId })
		}
	})
	const editDocument = api.knowledgeBase.edit.useMutation({
		onSuccess: () => {
			utils.knowledgeBase.list.invalidate({ teamId: document.teamId })
		}
	})
	const deleteDocument = api.knowledgeBase.delete.useMutation({
		onSuccess: () => {
			utils.knowledgeBase.list.invalidate({ teamId: document.teamId })
		}
	})

	return (
		<div className="group relative">
			<TooltipProvider>
				<Item variant="row" size="document">
					<Link
						to={document.slug}
						className="group grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_auto] items-center gap-3 focus-visible:outline-2 focus-visible:outline-ring"
					>
						<div className="grid min-w-0 grid-cols-[34px_minmax(0,1fr)] items-center gap-3">
							<div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-background-secondary text-icons transition">
								<KBIcon name={document.icon} className="size-4" />
							</div>
							<div className="min-w-0">
								<span className="block truncate text-sm font-medium text-text-main transition-colors group-hover:text-text-main">
									{document.title}
								</span>
								{document.description ? (
									<span className="block truncate text-xs font-medium text-text-sub">
										{document.description}
									</span>
								) : null}
								{document.accessRoles.length ? (
									<span className="mt-1 flex items-center gap-1 truncate text-[11px] font-medium text-text-small">
										<ShieldIcon className="size-3" /> Restricted to{" "}
										{document.accessRoles.map((role) => role.name).join(", ")}
									</span>
								) : null}
							</div>
						</div>
						<div className="hidden min-w-0 flex-row items-center justify-end gap-3 sm:flex">
							{document.author ? (
								<span className="max-w-32 truncate text-xs font-medium text-text-small">
									{document.author.name}
								</span>
							) : null}
							<Tooltip>
								<TooltipTrigger asChild>
									<span className="w-10 whitespace-nowrap text-right text-xs font-medium text-text-small">
										{shortRelativeTime(document.updatedAt)}
									</span>
								</TooltipTrigger>
								<TooltipContent>
									Updated at: {formatDate(document.updatedAt)}
								</TooltipContent>
							</Tooltip>
						</div>
					</Link>
					<div className="hidden shrink-0 sm:block">
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									variant="ghost"
									size="icon"
									aria-label="Document actions"
									onClick={(event) => event.preventDefault()}
								>
									<EllipsisIcon className="size-4" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								<DropdownMenuItem
									onClick={(event) => {
										event.preventDefault()
										toggleFavorite.mutate({
											teamId: document.teamId,
											documentId: document.id
										})
									}}
								>
									<StarIcon
										className={cn(
											"size-4",
											document.userHasFavorited &&
												"fill-yellow-300 text-yellow-300"
										)}
									/>
									{document.userHasFavorited ? "Unfavorite" : "Favorite"}{" "}
									document
								</DropdownMenuItem>
								{canEdit ? (
									<>
										<DropdownMenuItem
											onClick={(event) => {
												event.preventDefault()
												editDocument.mutate({
													teamId: document.teamId,
													documentId: document.id,
													pinned: !document.pinned
												})
											}}
										>
											<PinIcon className="size-4" />
											{document.pinned ? "Unpin" : "Pin"} document
										</DropdownMenuItem>
										<DropdownMenuItem disabled>
											<HistoryIcon className="size-4" />
											Document history
										</DropdownMenuItem>
										<DropdownMenuSeparator />
										<DropdownMenuItem
											variant="destructive"
											onClick={async (event) => {
												event.preventDefault()
												const confirmed = await confirmAction({
													title: "Delete document",
													message: "Delete this document?"
												})
												if (!confirmed) return
												deleteDocument.mutate({
													teamId: document.teamId,
													documentId: document.id
												})
											}}
										>
											<Trash2Icon className="size-4 text-destructive" />
											Delete document
										</DropdownMenuItem>
									</>
								) : null}
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				</Item>
			</TooltipProvider>
		</div>
	)
}
